import { z } from "zod";

// ---------------------------------------------------------------------------
// テーマ色（#191、docs/77、DB 20261016_1900。2026-10-16 射場さんの決定）。両製品で同じ5つ。
//   - 人（accounts.theme_color）に1つ＝PC・スマホ・どの会社でも同じ。選んだ瞬間に保存して、画面全体にすぐ当てる（PUT /account/preferences）
//   - 決まる順は 人の設定 → 製品の既定（現場力＝藍、営業力＝濃い青＝PRODUCTS[*].brand.theme）。ログイン前の画面には人の設定を当てない（製品の既定）
//   - 変わるもの：主ボタン・リンク・選択中のタブと行・フォーカスの枠・チェックボックスとスイッチの ON・アイコンの強調色（styles.css の --c-* の変数だけ）
//   - 変えないもの：上の帯（白）・紙 #FFFFFF・机 #F3F2EE・文字 #1A1A18／#55534C・罫線・朱 #9E2718・ロゴ。紙の PDF も変えない
//   - 色の値は styles.css の [data-theme] の行と同じ（apps/api/test/web-styles.test.ts ⑫ が見比べる）
// ---------------------------------------------------------------------------

export const ThemeColor = z.enum(["indigo", "blue", "teal", "green", "sumi"]);
export type ThemeColor = z.infer<typeof ThemeColor>;

export type ThemeSpec = {
  key: ThemeColor;
  /** 設定の画面に出す名前 */
  label: string;
  /** 主色（主ボタン・選択中のタブの字・フォーカスの枠・チェックボックスとスイッチの ON・アイコンの強調） */
  primary: string;
  /** 押下・乗せたとき（主ボタン） */
  hover: string;
  /** 淡い地色（選択中のタブ・行・淡いボタン） */
  container: string;
  /** リンク（どのテーマも下線あり。墨だけ藍＝主色の墨では文字と見分けにくい） */
  link: string;
};

/** 設定の画面の並び（docs/77 §2 のパレット） */
export const THEME_COLORS: readonly ThemeSpec[] = [
  { key: "indigo", label: "藍", primary: "#123C63", hover: "#0E3050", container: "#E7EEF4", link: "#123C63" },
  { key: "blue", label: "濃い青", primary: "#1A4F8C", hover: "#143F70", container: "#E6EEF7", link: "#1A4F8C" },
  { key: "teal", label: "青緑", primary: "#0E5A66", hover: "#0A4851", container: "#E2EFF1", link: "#0E5A66" },
  { key: "green", label: "濃い緑", primary: "#0A5C45", hover: "#074A37", container: "#E3F0EA", link: "#0A5C45" },
  { key: "sumi", label: "墨", primary: "#33312C", hover: "#22211E", container: "#EFEEE9", link: "#123C63" },
];

/**
 * 設定の画面に出さない色（消さない＝CSS・DB の CHECK・API は受ける。戻すときはここから外すだけ）。
 *   濃い緑：2026-10-16 の色覚の確かめ（docs/77 付録B）で、1型色覚の見え方で主色と朱の差 ΔE2000 が 11.3（ほかの色は 15.8 以上）＝
 *   主ボタンと朱の警告のしるしが見分けにくい。依頼の決まり（見分けにくければ非表示にして報告）どおり非表示
 */
export const THEME_HIDDEN: ReadonlySet<ThemeColor> = new Set<ThemeColor>(["green"]);

/** 設定の画面に並べる色（人がいま非表示の色を使っていれば、その色も並べる＝選んでいる色が消えて見えないように） */
export function themeChoices(current: ThemeColor | null | undefined): readonly ThemeSpec[] {
  return THEME_COLORS.filter((t) => !THEME_HIDDEN.has(t.key) || t.key === current);
}

export function themeSpec(key: ThemeColor): ThemeSpec {
  return THEME_COLORS.find((t) => t.key === key)!;
}

// ---------------------------------------------------------------------------
// 明るさ（#191 の2回目、docs/77 §8、DB 20261016_2100。2026-10-16 射場さんの決定）
//   - ライト／ダーク／端末に合わせる。既定はライト（端末がダークでも勝手に反転しない）。人（accounts.color_mode）に1つ・選んだ瞬間に保存・すぐ反映
//   - 端末に合わせる＝prefers-color-scheme を見て、OS の設定が変われば追従する
//   - ダークでもテーマ5色は選べる（主色は明るい側の値・主ボタンの文字は墨 #1A1A18）。上の帯は紙と同じ色・ロゴは暗い地用
//   - ライトのまま：ログイン前の画面・PDF・印刷・帳票のプレビュー・写真・地図
//   - 値は styles.css の html[data-mode="dark"] の行と同じ（apps/api/test/web-styles.test.ts ⑮ が見比べる）
// ---------------------------------------------------------------------------

export const ColorMode = z.enum(["light", "dark", "system"]);
export type ColorMode = z.infer<typeof ColorMode>;
/** 画面に当てる明るさ（system は端末の設定で light か dark に決まる） */
export type ResolvedColorMode = "light" | "dark";

export const COLOR_MODES: readonly { key: ColorMode; label: string }[] = [
  { key: "light", label: "ライト" },
  { key: "dark", label: "ダーク" },
  { key: "system", label: "端末に合わせる" },
];

/** ダークの土台の色（テーマで変わらない） */
export const DARK_BASE = {
  desk: "#141413",
  paper: "#1E1E1C",
  /** 浮いた面（カード・シート・ダイアログ・メニュー・引き出し・通知） */
  raised: "#262523",
  ink: "#ECEAE4",
  inkSub: "#B5B2A9",
  line1: "#3A3935",
  line2: "#4A4944",
  line3: "#6B6963",
  shu: "#E8907C",
  /** 朱の淡い地 */
  shuContainer: "#3A2420",
  /** 主ボタン・朱の塗りの上の文字（墨） */
  onPrimary: "#1A1A18",
} as const;

/** ダークのテーマの主色と淡い地色（選択中）。押下・ホバーは主色を 8% 明るく（themeHoverDark） */
export const DARK_THEME: Record<ThemeColor, { primary: string; container: string }> = {
  indigo: { primary: "#8FB4E3", container: "#1F2C3A" },
  blue: { primary: "#8FB8F0", container: "#1E2B3D" },
  teal: { primary: "#7FC4CE", container: "#1B2F33" },
  green: { primary: "#7FC9AD", container: "#1C3129" },
  sumi: { primary: "#D8D5CC", container: "#2A2926" },
};

/** 色を白に t（0〜1）だけ寄せる（#RRGGBB）。ダークの押下・ホバー＝主色を 8% 明るく */
export function mixWithWhite(hex: string, t: number): string {
  const ch = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return "#" + ch.map((v) => Math.round(v + (255 - v) * t).toString(16).padStart(2, "0")).join("").toUpperCase();
}
export function themeHoverDark(key: ThemeColor): string {
  return mixWithWhite(DARK_THEME[key].primary, 0.08);
}

export const THEME_MSG = {
  title: "テーマ色",
  /** 製品の既定の色の名前の下の印 */
  defaultMark: "既定",
  /** 選んだ色を保存できなかったとき（色は前に戻す） */
  saveFailed: "テーマ色を保存できませんでした",
  /** 明るさの行（#191 の2回目） */
  mode: "明るさ",
  modeSaveFailed: "明るさを保存できませんでした",
} as const;
